import React, { Fragment, useMemo, useState } from 'react';
import {
  List,
  ListSubheader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  Stack,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@iconify/icons-mdi/chevron-down';
import OffAirplane from '@iconify/icons-mdi/airplane-off';
import Airplane from '@iconify/icons-mdi/airplane';
import SpeedOmeter from '@iconify/icons-mdi/speedometer';
import FilterHdr from '@iconify/icons-mdi/image-filter-hdr';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { devicesActions } from '../../store';
import LastUpdateFormatter from '../../common/utils/formatTime';
import { speedConverter, altitudeConverter } from '../../common/utils/unitConverter';
import { useTranslation } from '../../common/LocalizationProvider';
import {
  selectDevicesArray,
  selectGroupSelectedId,
  selectPositionsArray,
  selectSession
} from '../../createSelectorUtils';

export default function SideBarMapLive() {
  const t = useTranslation();
  const dispatch = useDispatch();

  const session = useSelector(selectSession);
  const groupId = useSelector(selectGroupSelectedId);
  const devicesData = useSelector(selectDevicesArray);
  const devices = useMemo(
    () =>
      session.administrator
        ? devicesData
        : devicesData.filter((device) =>
            groupId === 0
              ? device.groupId === 0 ||
                session.permission.find((item) => item.groupid === device.groupId) === undefined
              : device.groupId === groupId
          ),
    [devicesData, groupId, session.administrator, session.permission]
  );

  const positions = useSelector(selectPositionsArray);

  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = (panel, deviceId, device) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);

    if (newExpanded) {
      dispatch(devicesActions.select(device));
    } else {
      dispatch(devicesActions.select([]));
    }
  };

  return (
    <List
      sx={{ width: '100%', bgcolor: 'background.paper', overflow: 'auto' }}
      component="nav"
      aria-labelledby="nested-list-subheader"
      subheader={
        <ListSubheader component="div" id="nested-list-subheader">
          {t('LM_DevicesList')}
        </ListSubheader>
      }
    >
      {devices
        .filter((device) => device.status === 'online' && !device.disabled)
        .map((device) =>
          positions
            .filter((position) => position.deviceId === device.id)
            .map((position) => (
              <Fragment key={device.id}>
                <Accordion
                  expanded={expanded === `panel1${device.id}`}
                  onChange={handleExpandClick(`panel1${device.id}`, device.id, device)}
                >
                  <AccordionSummary
                    expandIcon={<Icon icon={ExpandMoreIcon} width={24} height={24} />}
                    aria-controls="panel1bh-content"
                    id="panel1bh-header"
                  >
                    <Stack direction={{ xs: 'row', sm: 'row' }} spacing={5}>
                      <Avatar sx={{ bgcolor: 'green' }}>
                        <Icon icon={Airplane} width={30} height={30} />
                      </Avatar>

                      <LastUpdateFormatter device={device} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: '20%' }}>
                    <Stack
                      direction={{ xs: 'row' }}
                      spacing={0}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'column' }}
                        spacing={0}
                        alignItems="center"
                      >
                        <Icon icon={FilterHdr} width={25} height={25} />
                        <ListItemText
                          primary={t('LM_Elevation')}
                          secondary={`${altitudeConverter(
                            position.altitude,
                            session.uniteAltitude
                          )} ${session.uniteAltitude}`}
                        />
                      </Stack>
                      <Stack
                        direction={{ xs: 'column', sm: 'column' }}
                        spacing={0}
                        alignItems="center"
                      >
                        <Icon icon={SpeedOmeter} width={25} height={25} />
                        <ListItemText
                          primary={t('LM_Speed')}
                          secondary={`${speedConverter(position.speed, session.uniteVitesse)} ${
                            session.uniteVitesse
                          }`}
                        />
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Fragment>
            ))
        )}
      {devices
        .filter((device) => device.status !== 'online' && !device.disabled)
        .sort((a, b) => new Date(b.lastUpdate) - new Date(a.lastUpdate))
        .map((device) =>
          positions
            .filter((position) => position.deviceId === device.id)
            .map((position) => (
              <Fragment key={device.id}>
                <Accordion
                  expanded={expanded === `panel1${device.id}`}
                  onChange={handleExpandClick(`panel1${device.id}`, device.id, device)}
                >
                  <AccordionSummary
                    expandIcon={<Icon icon={ExpandMoreIcon} width={24} height={24} />}
                    aria-controls="panel1bh-content"
                    id="panel1bh-header"
                  >
                    <Stack direction={{ xs: 'row', sm: 'row' }} spacing={5}>
                      {(device.status === 'unknown' && position.attributes.motion) ||
                      (device.status === 'offline' &&
                        position.attributes.motion &&
                        position.attributes.ignition) ? (
                        <Avatar sx={{ bgcolor: 'orange' }}>
                          <Icon icon={Airplane} width={30} height={30} />
                        </Avatar>
                      ) : (
                        <Avatar sx={{ bgcolor: 'red' }}>
                          <Icon icon={OffAirplane} width={30} height={30} />
                        </Avatar>
                      )}

                      <LastUpdateFormatter device={device} />
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: '20%' }}>
                    <Stack
                      direction={{ xs: 'row' }}
                      spacing={0}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack
                        direction={{ xs: 'column', sm: 'column' }}
                        spacing={0}
                        alignItems="center"
                      >
                        <Icon icon={FilterHdr} width={25} height={25} />
                        <ListItemText
                          primary={t('LM_Elevation')}
                          secondary={`${altitudeConverter(
                            position.altitude,
                            session.uniteAltitude
                          )} ${session.uniteAltitude}`}
                        />
                      </Stack>
                      <Stack
                        direction={{ xs: 'column', sm: 'column' }}
                        spacing={0}
                        alignItems="center"
                      >
                        <Icon icon={SpeedOmeter} width={25} height={25} />
                        <ListItemText
                          primary={t('LM_Speed')}
                          secondary={`${speedConverter(position.speed, session.uniteVitesse)} ${
                            session.uniteVitesse
                          }`}
                        />
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              </Fragment>
            ))
        )}
    </List>
  );
}
